import { Component, Ref, Vue, Watch, Prop } from 'vue-property-decorator'
import Inquiry from '@/inquiries/models/inquiry'
import inquiriesService from '@/inquiries/services/inquiries-service'
import InquiryDialog from '@/inquiries/components/inquiry-dialog/inquiry-dialog'
import InquiryContactDialog from '@/inquiries/components/inquiry-contact/inquiry-contact'
import InquiriesFilterRequest from '@/inquiries/models/inquiries-filter-request'
import { InquiryState } from '@/inquiries/models/inquiry-state'
import InquiryContact from '@/inquiries/models/Inquiry-contact'
import * as texts from '@/app/utils/texts'
import accountService from '@/users/services/account-service'
import ClosedDialog from '@/inquiries/components/closed-dialog/closed-dialog'
import ConfirmDialog from '@/app/components/confirm-dialog/confirm-dialog'

@Component
export default class InquiriesSearch extends Vue {
  @Ref()
  private dialog!: InquiryDialog

  @Ref()
  private contactDialog!: InquiryContactDialog

  @Ref()
  private closedDialog!: ClosedDialog

  @Ref()
  private sendOfferDialog!: ConfirmDialog

  @Ref()
  private removeDialog!: ConfirmDialog

  private loading: boolean = false
  private contactLoading: boolean = false
  private inquiries: Inquiry[] = []
  private pendingContacts: InquiryContact[] = []
  private filter: InquiriesFilterRequest = new InquiriesFilterRequest()

  private offerText = texts.AskSendOfferText

  private inquiryTypeFilters = [
    { text: 'alle', value: '' },
    { text: 'Ortsgebunden', value: 'only-with-location' },
    { text: 'Nicht ortsgebunden', value: 'only-without-location' },
  ]

  private async findAsync() {
    this.loading = true
    // Workaround for empty radiusOfInterest.
    if (!this.filter.radiusOfInterest) {
      this.filter.radiusOfInterest = 0
    }
    this.filter.includeContacts = true
    const response = await inquiriesService.findAllAsync(this.filter)
    this.inquiries = response.items
    this.filter.pagination.totalItems = response.totalItems
    this.pendingContacts = await inquiriesService.findAllPendingContactsAsync()
    this.loading = false
  }

  private async filterForType() {
    this.filter.typeFilter = this.filter.typeFilter || ''
    await this.findAsync()
  }

  private openNew() {
    const inquiry = new Inquiry()
    inquiry.creatorId = this.$auth.user
    this.dialog.openNew(inquiry)
  }

  private openEdit(inquiry: Inquiry) {
    this.dialog.openEdit(inquiry)
  }

  private async create(inquiry: Inquiry) {
    await inquiriesService.saveAsync(inquiry)
    await this.findAsync()
  }

  private async update(inquiry: Inquiry) {
    await inquiriesService.updateAsync(inquiry)
    await this.findAsync()
  }

  private openContactDialog(inquiry: Inquiry) {
    this.contactDialog.open(inquiry)
  }

  private openClosedDialog() {
    this.closedDialog.open()
  }

  private contactConfirm(inquiry: Inquiry) {
    this.sendOfferDialog.open(inquiry)
  }

  private removeConfirm(inquiry: Inquiry) {
    this.removeDialog.open(inquiry)
  }

  private async contact(inquiry: Inquiry) {
    this.contactLoading = true
    await inquiriesService.contactAsync(this.$auth.user, inquiry)
    await this.findAsync()
    this.contactLoading = false
  }

  private async close(inquiry: Inquiry) {
    if (confirm('Wirklich abschließen?')) {
      await inquiriesService.closeAsync(inquiry)
      await this.findAsync()
    }
  }

  private async reactivate(inquiry: Inquiry) {
    await inquiriesService.reactivateAsync(inquiry)
    await this.findAsync()
  }

  private async remove(inquiry: Inquiry) {
    await inquiriesService.removeAsync(inquiry.id)
    await this.findAsync()
  }

  private async mounted() {
    this.filter.creatorId = null
    this.filter.takerId = null
    this.filter.state = InquiryState.Open
    this.filter.ignoreAccountSettings = true

    this.loading = true
    // Store the inquiry ID and current route BEFORE making any API calls
    const inquiryId = this.$route.params.inquiryId
    const currentFullPath = this.$route.fullPath // Store this immediately

    try {
      // user data is in store and does not mutate over multiple tabs
      await accountService.getCurrent()
      this.filter.radiusOfInterest = this.$user.data.radiusOfInterest
    } catch (error) {
      // If account service fails, preserve the current route for redirect after login
      if (inquiryId) {
        // Store the route path that was captured before the API call
        localStorage.setItem('pendingRedirect', currentFullPath)
      }
      // Let the error bubble up to trigger the login redirect
      this.loading = false
      throw error
    }

    await this.findAsync()

    if (!inquiryId) {
      this.loading = false
      return
    }

    this.loading = false
    const inquiry = this.inquiries.find((i) => i.id === inquiryId)
    if (inquiry) {
      this.openContactDialog(inquiry)
    } else {
      this.openClosedDialog()
    }
  }
}
